import { ArrowRight, CircleHelp, ExternalLink, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { cn, IconButton } from './primitives';

export interface ContextualHelpAction {
  label: string;
  href?: string;
  onClick?: () => void;
  target?: string;
  rel?: string;
}

export interface ContextualHelpProps {
  title: string;
  description: ReactNode;
  action?: ContextualHelpAction;
  ariaLabel?: string;
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  className?: string;
  popoverClassName?: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  maxWidth?: number;
}

interface PopoverPosition {
  top: number;
  left: number;
  width: number;
  side: 'top' | 'bottom';
}

const VIEWPORT_GUTTER = 12;
const POPOVER_GAP = 8;
const DEFAULT_POPOVER_MAX_WIDTH = 360;

/**
 * A compact, reusable explanation control for terms and fields that may be
 * unfamiliar to first-time users.
 */
export function ContextualHelp({
  title,
  description,
  action,
  ariaLabel,
  side = 'bottom',
  align = 'center',
  className,
  popoverClassName,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  maxWidth = DEFAULT_POPOVER_MAX_WIDTH
}: ContextualHelpProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const popoverId = useId();
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange]
  );

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;

    const triggerRect = trigger.getBoundingClientRect();
    const width = Math.min(maxWidth, window.innerWidth - VIEWPORT_GUTTER * 2);
    const height = popover.offsetHeight;

    let left =
      align === 'start'
        ? triggerRect.left
        : align === 'end'
          ? triggerRect.right - width
          : triggerRect.left + triggerRect.width / 2 - width / 2;
    left = Math.min(Math.max(left, VIEWPORT_GUTTER), window.innerWidth - width - VIEWPORT_GUTTER);

    let resolvedSide = side;
    let top =
      side === 'bottom' ? triggerRect.bottom + POPOVER_GAP : triggerRect.top - height - POPOVER_GAP;

    const fitsAbove = triggerRect.top - height - POPOVER_GAP >= VIEWPORT_GUTTER;
    const fitsBelow =
      triggerRect.bottom + height + POPOVER_GAP <= window.innerHeight - VIEWPORT_GUTTER;

    if (side === 'bottom' && !fitsBelow && fitsAbove) {
      resolvedSide = 'top';
      top = triggerRect.top - height - POPOVER_GAP;
    } else if (side === 'top' && !fitsAbove && fitsBelow) {
      resolvedSide = 'bottom';
      top = triggerRect.bottom + POPOVER_GAP;
    }

    top = Math.min(
      Math.max(top, VIEWPORT_GUTTER),
      Math.max(VIEWPORT_GUTTER, window.innerHeight - height - VIEWPORT_GUTTER)
    );
    setPosition({ top, left, width, side: resolvedSide });
  }, [align, maxWidth, side]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      updatePosition();
      popoverRef.current?.focus();
    });
    const handleViewportChange = () => updatePosition();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, setOpen]);

  const handleAction = () => {
    action?.onClick?.();
    setOpen(false);
  };
  const actionOpensNewTab = action?.target === '_blank';
  const actionRel = action?.rel ?? (actionOpensNewTab ? 'noreferrer noopener' : undefined);
  const popoverStyle: CSSProperties = {
    top: position?.top ?? 0,
    left: position?.left ?? 0,
    width: position?.width,
    visibility: position ? 'visible' : 'hidden'
  };

  return (
    <span className={cn('dd-contextual-help', className)}>
      <IconButton
        ref={triggerRef}
        className="dd-contextual-help-trigger"
        variant="ghost"
        aria-label={ariaLabel ?? `Spiegazione: ${title}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? popoverId : undefined}
        title={`Informazioni: ${title}`}
        onClick={() => setOpen(!open)}
      >
        <CircleHelp aria-hidden="true" />
      </IconButton>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              className={cn('dd-contextual-help-popover', popoverClassName)}
              id={popoverId}
              role="dialog"
              aria-modal="false"
              aria-labelledby={titleId}
              aria-describedby={descriptionId}
              data-side={position?.side ?? side}
              style={popoverStyle}
              tabIndex={-1}
            >
              <div className="dd-contextual-help-header">
                <span className="dd-contextual-help-symbol" aria-hidden="true">
                  <CircleHelp />
                </span>
                <h2 id={titleId}>{title}</h2>
                <IconButton
                  className="dd-contextual-help-close"
                  variant="ghost"
                  aria-label={`Chiudi la spiegazione: ${title}`}
                  onClick={() => {
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                >
                  <X aria-hidden="true" />
                </IconButton>
              </div>
              <div className="dd-contextual-help-description" id={descriptionId}>
                {description}
              </div>
              {action ? (
                <div className="dd-contextual-help-footer">
                  {action.href ? (
                    <a
                      className="dd-contextual-help-action"
                      href={action.href}
                      target={action.target}
                      rel={actionRel}
                      onClick={handleAction}
                    >
                      <span>{action.label}</span>
                      {actionOpensNewTab ? (
                        <>
                          <ExternalLink aria-hidden="true" />
                          <span className="dd-visually-hidden">Si apre in una nuova scheda</span>
                        </>
                      ) : (
                        <ArrowRight aria-hidden="true" />
                      )}
                    </a>
                  ) : (
                    <button
                      className="dd-contextual-help-action"
                      type="button"
                      onClick={handleAction}
                    >
                      <span>{action.label}</span>
                      <ArrowRight aria-hidden="true" />
                    </button>
                  )}
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}
